<template>
  <div>
    <a-modal :visible="visible" :destroy-on-close="true" :title="modalTitle" @cancel="closeModal">
      <a-form layout="horizontal">
        <a-form-item
          :label-col="{ span: 8 }"
          :wrapper-col="{ span: 16 }"
          label="Destination project"
        >
          <a-select
            v-model="form.destination_project_id"
            show-search
            option-filter-prop="children"
            placeholder="Select destination project"
          >
            <a-select-option
              v-for="project in destinationProjects"
              :key="project.id"
              :value="project.id"
            >
              {{ project.name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item
          v-if="props.sourceContext?.sourceType === 'report'"
          :label-col="{ span: 8 }"
          :wrapper-col="{ span: 16 }"
          label="Source"
        >
          <a-select v-model="form.source_key" placeholder="Select source">
            <a-select-option v-for="option in sourceOptions" :key="option.key" :value="option.key">
              {{ option.label }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item
          v-if="showSourceReportSelection"
          :label-col="{ span: 8 }"
          :wrapper-col="{ span: 16 }"
          label="Source report"
        >
          <a-select
            v-model="form.source_report_ids"
            mode="multiple"
            placeholder="Select source reports"
          >
            <a-select-option
              v-for="option in sourceReportOptions"
              :key="option.report_id"
              :value="option.report_id"
            >
              {{ option.label }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item
          v-if="props.sourceContext?.sourceType === 'subproject_all'"
          :label-col="{ span: 8 }"
          :wrapper-col="{ span: 16 }"
          label="Total hours"
        >
          <div>{{ selectedSubprojectTransferTotal }}h</div>
        </a-form-item>

        <a-form-item
          v-if="isAmountRequired"
          :label-col="{ span: 8 }"
          :wrapper-col="{ span: 16 }"
          label="Amount"
        >
          <a-input-number
            v-model="form.amount"
            :min="0"
            :step="1"
            :precision="0"
            class="w-100"
            placeholder="Amount"
          />
        </a-form-item>
      </a-form>

      <div v-if="backendErrorMessage">
        <a-alert type="error" show-icon :message="backendErrorMessage" />
        <ul v-if="backendValidationErrors.length" class="text-danger mt-2 pl-4">
          <li v-for="(error, index) in backendValidationErrors" :key="index">
            {{ error }}
          </li>
        </ul>
      </div>

      <template #footer>
        <div>
          <a-button @click="closeModal">Cancel</a-button>
          <a-button
            type="primary"
            :loading="previewLoading || executeLoading"
            :disabled="!hasRequiredFields"
            @click="handleExecute"
          >
            Transfer
          </a-button>
        </div>
      </template>
    </a-modal>

    <a-modal
      :visible="confirmTransferVisible"
      :confirm-loading="executeLoading"
      :title="confirmTransferTitle"
      ok-text="Transfer"
      cancel-text="Cancel"
      @ok="confirmExecuteTransfer"
      @cancel="closeConfirmTransferModal"
    >
      <div>
        <strong>Summary</strong>
        <ul class="mt-2 pl-4 mb-0">
          <li v-for="(item, index) in confirmTransferSummaryItems" :key="`summary-${index}`">
            {{ item }}
          </li>
        </ul>
      </div>

      <div v-if="confirmTransferMappings.length" class="mt-3">
        <strong>These reports will be moved:</strong>
        <ul class="mt-2 pl-4 mb-0">
          <li v-for="(mapping, index) in confirmTransferMappings" :key="`mapping-${index}`">
            {{ mapping.startDate }} • {{ mapping.roleTitle }} ({{ mapping.userName }}) •
            {{ mapping.amount }}h
          </li>
        </ul>
      </div>

      <a-alert
        class="mt-3"
        type="info"
        show-icon
        message="If the destination project does not already have a matching role, the transfer will create a transfer-only role that cannot be selected for new time reports."
      />
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { getProjectsCompactList } from "@/api";
import { ApiError, extractApiErrorMessage } from "@/utils";
import {
  MinimalProjectInterface,
  NewSubprojectInterface,
  TransferSourceContext,
  TransferPayloadInterface,
} from "../types";
import { ReportInterface } from "@/modules/reports/types";
import { apiPreviewProjectTransferBatch, apiStoreProjectTransferBatch } from "../_utils/api";

interface TransferFormState {
  source_key?: string;
  source_report_ids?: Array<number | string>;
  destination_project_id?: number | string;
  amount?: number;
}

interface TransferSourceOption {
  key: string;
  label: string;
  amount?: number;
  source_type: "report" | "subproject";
  source_subproject_id?: number;
}

interface TransferSourceReportOption {
  report_id: number;
  label: string;
  subproject_amount?: number;
}

interface TransferSourceReportOptionWithSort {
  report_id: number;
  label: string;
  subproject_amount?: number;
  sort_time: number;
}

interface TransferConfirmMapping {
  amount: number;
  startDate: string;
  roleTitle: string;
  userName: string;
}

// Props
const props = defineProps({
  visible: { type: Boolean, default: false },
  projectId: { type: Number, default: undefined },
  sourceContext: { type: Object as () => TransferSourceContext | undefined, default: undefined },
});

// Emits
const emits = defineEmits(["close", "executed"]);

// Data properties
const destinationProjects = ref<Array<MinimalProjectInterface>>([]);
const form = ref<TransferFormState>({});
const previewLoading = ref<boolean>(false);
const executeLoading = ref<boolean>(false);
const backendErrorMessage = ref<string>("");
const backendValidationErrors = ref<Array<string>>([]);
const confirmTransferVisible = ref<boolean>(false);
const confirmTransferTitle = ref<string>("");
const confirmTransferSummaryItems = ref<Array<string>>([]);
const confirmTransferMappings = ref<Array<TransferConfirmMapping>>([]);
const pendingTransferPayloads = ref<Array<TransferPayloadInterface>>([]);

// Computed properties
const modalTitle = computed((): string => {
  switch (props.sourceContext?.sourceType) {
    case "report":
      return "Transfer report";
    case "subproject_all":
      return "Transfer all subproject hours";
    default:
      return "Transfer subproject hours";
  }
});

const isAmountRequired = computed((): boolean => {
  return props.sourceContext?.sourceType === "report";
});

const normalizeId = (value: unknown): number | undefined => {
  const normalizedValue = Number(value);
  if (!Number.isInteger(normalizedValue) || normalizedValue <= 0) {
    return undefined;
  }
  return normalizedValue;
};

const defaultSourceReportId = computed((): number | undefined => {
  return (
    normalizeId(props.sourceContext?.report?.id) ||
    normalizeId(props.sourceContext?.subproject?.report?.id) ||
    normalizeId(props.sourceContext?.subproject?.report_id)
  );
});

const selectedSourceReportIds = computed((): Array<number> => {
  if (props.sourceContext?.sourceType !== "subproject_all") {
    return [];
  }
  if (form.value.source_report_ids === undefined) {
    return defaultSourceReportId.value ? [defaultSourceReportId.value] : [];
  }
  const ids = form.value.source_report_ids
    .map((reportId) => normalizeId(reportId))
    .filter((reportId): reportId is number => !!reportId);
  return ids;
});

const sourceSubprojectId = computed((): number | undefined => {
  return normalizeId(props.sourceContext?.subproject?.id);
});

const normalizeAmount = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (value && typeof value === "object" && "value" in value) {
    const normalizedValue = Number(value.value);
    if (Number.isFinite(normalizedValue)) {
      return normalizedValue;
    }
  }
  const normalizedValue = Number(value);
  if (Number.isFinite(normalizedValue)) {
    return normalizedValue;
  }
  return undefined;
};

const sourceOptions = computed((): Array<TransferSourceOption> => {
  if (props.sourceContext?.sourceType !== "report") {
    return [];
  }

  const subprojectReports = props.sourceContext.report?.subproject_reports || [];
  return subprojectReports
    .map((subprojectReport) => {
      const subprojectId = normalizeId(subprojectReport.subproject_id);
      const subprojectAmount = normalizeAmount(subprojectReport.amount);
      if (!subprojectId || !subprojectAmount || subprojectAmount <= 0) {
        return undefined;
      }
      return {
        key: `subproject-${subprojectId}`,
        label: `${subprojectReport.name || "Subproject"} (${subprojectAmount}h)`,
        amount: subprojectAmount,
        source_type: "subproject" as const,
        source_subproject_id: subprojectId,
      };
    })
    .filter((option): option is TransferSourceOption => !!option);
});

const selectedDestinationProject = computed((): MinimalProjectInterface | undefined => {
  const destinationProjectId = normalizeId(form.value.destination_project_id);
  if (!destinationProjectId) {
    return undefined;
  }
  return destinationProjects.value.find((project) => project.id === destinationProjectId);
});

const getDestinationProjectIdForSourceKey = (sourceKey?: string): number | undefined => {
  if (props.sourceContext?.sourceType !== "report" || !sourceKey) {
    return undefined;
  }

  const selectedOption = sourceOptions.value.find((option) => option.key === sourceKey);
  if (!selectedOption?.source_subproject_id) {
    return undefined;
  }

  const sourceSubproject = (props.sourceContext.subprojects || []).find(
    (subproject: NewSubprojectInterface) =>
      normalizeId(subproject.id) === selectedOption.source_subproject_id
  );
  const destinationProjectId = normalizeId(sourceSubproject?.destination_project_id);
  if (!destinationProjectId) {
    return undefined;
  }
  const existsInDestinationProjects = destinationProjects.value.some(
    (project) => project.id === destinationProjectId
  );
  if (!existsInDestinationProjects) {
    return undefined;
  }
  return destinationProjectId;
};

const applyDestinationProjectForSource = (sourceKey?: string): void => {
  const destinationProjectId = getDestinationProjectIdForSourceKey(sourceKey);
  if (destinationProjectId) {
    form.value.destination_project_id = destinationProjectId;
  }
};

const candidateSubprojectReports = computed((): Array<ReportInterface> => {
  if (props.sourceContext?.sourceType !== "subproject_all") {
    return [];
  }

  if (props.sourceContext?.sourceReports?.length) {
    return props.sourceContext.sourceReports;
  }

  return props.sourceContext.report ? [props.sourceContext.report] : [];
});

const getSubprojectAmountForReport = (report: ReportInterface): number | undefined => {
  if (!sourceSubprojectId.value) {
    return undefined;
  }

  const subprojectReport = (report.subproject_reports || []).find(
    (item) => normalizeId(item.subproject_id) === sourceSubprojectId.value
  );
  return normalizeAmount(subprojectReport?.amount);
};

const getSortTimeForReport = (report: ReportInterface): number => {
  if (!report.start_date) {
    return Number.MAX_SAFE_INTEGER;
  }
  const time = Date.parse(report.start_date);
  return Number.isFinite(time) ? time : Number.MAX_SAFE_INTEGER;
};

const sourceReportOptions = computed((): Array<TransferSourceReportOption> => {
  return candidateSubprojectReports.value
    .map((report) => {
      const reportId = normalizeId(report.id);
      if (!reportId) {
        return;
      }
      const startDate = report.start_date || "Unknown date";
      const roleTitle = report.role?.title || "No role";
      const userName = report.user?.name || "Unknown user";
      const subprojectAmount = getSubprojectAmountForReport(report);
      return {
        report_id: reportId,
        label: `${startDate} • ${roleTitle} • ${userName} (${subprojectAmount || 0}h)`,
        subproject_amount: subprojectAmount,
        sort_time: getSortTimeForReport(report),
      };
    })
    .filter((option): option is TransferSourceReportOptionWithSort => !!option)
    .sort((a, b) => a.sort_time - b.sort_time || a.report_id - b.report_id)
    .map(({ sort_time, ...option }) => option);
});

const showSourceReportSelection = computed((): boolean => {
  return sourceReportOptions.value.length > 1;
});

const selectedSubprojectTransferTotal = computed((): number => {
  if (props.sourceContext?.sourceType !== "subproject_all") {
    return 0;
  }

  const selectedIds = new Set(selectedSourceReportIds.value);
  return sourceReportOptions.value.reduce((total, option) => {
    if (!selectedIds.has(option.report_id)) {
      return total;
    }
    return total + (option.subproject_amount || 0);
  }, 0);
});

const selectedSourceOption = computed((): TransferSourceOption | undefined => {
  if (props.sourceContext?.sourceType !== "report") {
    return undefined;
  }
  return sourceOptions.value.find((option) => option.key === form.value.source_key);
});

const hasRequiredFields = computed((): boolean => {
  if (!props.sourceContext || !normalizeId(form.value.destination_project_id)) {
    return false;
  }
  if (props.sourceContext.sourceType === "report" && !defaultSourceReportId.value) {
    return false;
  }
  if (props.sourceContext.sourceType === "report" && sourceOptions.value.length === 0) {
    return false;
  }
  if (
    props.sourceContext.sourceType === "report" &&
    (!form.value.source_key || !selectedSourceOption.value)
  ) {
    return false;
  }
  if (
    props.sourceContext.sourceType === "subproject_all" &&
    selectedSourceReportIds.value.length === 0
  ) {
    return false;
  }
  if (props.sourceContext.sourceType === "subproject_all" && !sourceSubprojectId.value) {
    return false;
  }
  if (isAmountRequired.value && (form.value.amount === undefined || form.value.amount <= 0)) {
    return false;
  }
  return true;
});

// Watchers
watch(
  () => props.visible,
  async (visible: boolean) => {
    if (!visible) {
      resetState();
      return;
    }

    resetState();
    try {
      await loadProjects();
      initializeForm();
    } catch (error) {
      handleApiError(error, "Could not initialize transfer form.");
    }
  }
);

watch(
  () => form.value.destination_project_id,
  () => {
    clearErrors();
  }
);

watch(
  () => form.value.amount,
  () => {
    clearErrors();
  }
);

// Component methods
const loadProjects = async (): Promise<void> => {
  const projects = await getProjectsCompactList();
  destinationProjects.value = projects.filter((project) => project.id !== props.projectId);
};

const initializeForm = (): void => {
  const defaultSourceKey =
    props.sourceContext?.sourceType === "report" ? sourceOptions.value[0]?.key : undefined;
  const defaultSourceReportIds =
    props.sourceContext?.sourceType === "subproject_all"
      ? sourceReportOptions.value.length
        ? sourceReportOptions.value.map((option) => option.report_id)
        : defaultSourceReportId.value
          ? [defaultSourceReportId.value]
          : []
      : [];
  form.value = {
    source_key: defaultSourceKey,
    source_report_ids: defaultSourceReportIds,
    destination_project_id:
      props.sourceContext?.sourceType === "subproject_all"
        ? normalizeId(props.sourceContext?.subproject?.destination_project_id)
        : getDestinationProjectIdForSourceKey(defaultSourceKey),
    amount: isAmountRequired.value ? getDefaultAmount(defaultSourceKey) : undefined,
  };
};

const getDefaultAmount = (sourceKey?: string): number | undefined => {
  if (props.sourceContext?.sourceType === "report") {
    const selectedOption = sourceOptions.value.find((option) => option.key === sourceKey);
    return selectedOption?.amount;
  }

  const amount = props.sourceContext.report?.amount;
  if (typeof amount === "number") {
    return amount;
  }
  if (amount && typeof amount === "object" && "value" in amount) {
    return Number(amount.value);
  }
  return undefined;
};

watch(
  () => form.value.source_key,
  (sourceKey) => {
    if (props.sourceContext?.sourceType !== "report") {
      return;
    }
    form.value.amount = getDefaultAmount(sourceKey);
    applyDestinationProjectForSource(sourceKey);
    clearErrors();
  }
);

watch(
  () => form.value.source_report_ids,
  () => {
    if (props.sourceContext?.sourceType !== "subproject_all") {
      return;
    }
    clearErrors();
  }
);

const getSubprojectAmountForReportId = (sourceReportId: number): number => {
  const reportOption = sourceReportOptions.value.find(
    (option) => option.report_id === sourceReportId
  );
  return reportOption?.subproject_amount || 0;
};

const getSourceReportById = (sourceReportId: number): ReportInterface | undefined => {
  const reports = props.sourceContext?.sourceReports?.length
    ? props.sourceContext.sourceReports
    : props.sourceContext?.report
      ? [props.sourceContext.report]
      : [];
  return reports.find((report) => normalizeId(report.id) === sourceReportId);
};

const buildPayload = (
  sourceReportId: number,
  amountOverride?: number
): TransferPayloadInterface | null => {
  const destinationProjectId = normalizeId(form.value.destination_project_id);
  if (!props.sourceContext || !props.projectId || !destinationProjectId || !sourceReportId) {
    return null;
  }

  const payload: TransferPayloadInterface = {
    source_type: props.sourceContext.sourceType,
    source_report_id: sourceReportId,
    destination_project_id: destinationProjectId,
  };

  if (props.sourceContext.sourceType === "report") {
    if (!selectedSourceOption.value) {
      return null;
    }
    payload.source_type = selectedSourceOption.value.source_type;
    if (selectedSourceOption.value.source_type === "subproject") {
      payload.source_subproject_id = selectedSourceOption.value.source_subproject_id;
    }
  } else if (props.sourceContext.sourceType === "subproject_all") {
    if (!sourceSubprojectId.value) {
      return null;
    }
    payload.source_subproject_id = sourceSubprojectId.value;
  }

  if (typeof amountOverride === "number") {
    payload.amount = amountOverride;
  } else if (isAmountRequired.value && form.value.amount !== undefined) {
    payload.amount = form.value.amount;
  }

  return payload;
};

const buildPayloads = (): Array<TransferPayloadInterface> => {
  if (!props.sourceContext) {
    return [];
  }

  if (props.sourceContext.sourceType === "report") {
    if (!defaultSourceReportId.value) {
      return [];
    }
    const payload = buildPayload(defaultSourceReportId.value);
    return payload ? [payload] : [];
  }

  return selectedSourceReportIds.value
    .map((reportId) => buildPayload(reportId))
    .filter((payload): payload is TransferPayloadInterface => !!payload);
};

const getSourceReportDetails = (
  sourceReportId: number
): { startDate: string; roleTitle: string; userName: string } => {
  const sourceReport = getSourceReportById(sourceReportId);
  return {
    startDate: sourceReport?.start_date || "Unknown date",
    roleTitle: sourceReport?.role?.title || "No role",
    userName: sourceReport?.user?.name || "Unknown user",
  };
};

const getConfirmAmountForPayload = (payload: TransferPayloadInterface): number => {
  if (typeof payload.amount === "number") {
    return payload.amount;
  }
  if (payload.source_type === "subproject" || payload.source_type === "subproject_all") {
    return getSubprojectAmountForReportId(payload.source_report_id || 0);
  }
  return 0;
};

const buildConfirmStateFromPayloads = (
  payloads: Array<TransferPayloadInterface>
): { title: string; summaryItems: Array<string>; mappings: Array<TransferConfirmMapping> } => {
  const count = payloads.length;
  const totalAmount = payloads.reduce(
    (sum, payload) => sum + getConfirmAmountForPayload(payload),
    0
  );
  const mappings = payloads.map((payload) => {
    const amount = getConfirmAmountForPayload(payload);
    const sourceDetails = getSourceReportDetails(payload.source_report_id || 0);
    return { amount, ...sourceDetails };
  });
  const summaryItems = [
    `${count} source report${count > 1 ? "s" : ""}`,
    `Destination project: ${selectedDestinationProject.value?.name || "Unknown project"}`,
    totalAmount > 0 ? `Total amount: ${totalAmount}h` : undefined,
  ].filter((item): item is string => !!item);
  return {
    title: `Transfer ${count} source report${count > 1 ? "s" : ""}?`,
    summaryItems,
    mappings,
  };
};

const previewPayloads = async (): Promise<Array<TransferPayloadInterface> | null> => {
  clearErrors();
  if (!hasRequiredFields.value) {
    backendErrorMessage.value = "Please complete required transfer fields.";
    return null;
  }
  if (
    isAmountRequired.value &&
    form.value.amount !== undefined &&
    !Number.isInteger(form.value.amount)
  ) {
    backendErrorMessage.value = "Amount must be a whole number.";
    return null;
  }

  const payloads = buildPayloads();
  if (!payloads.length || !props.projectId) {
    return null;
  }

  previewLoading.value = true;
  try {
    await apiPreviewProjectTransferBatch(props.projectId, payloads);
    return payloads;
  } catch (error) {
    handleApiError(error, "Could not preview transfer.");
    return null;
  } finally {
    previewLoading.value = false;
  }
};

const executePayloads = async (payloads: Array<TransferPayloadInterface>): Promise<void> => {
  if (!props.projectId || !payloads.length) {
    return;
  }

  clearErrors();
  executeLoading.value = true;
  try {
    await apiStoreProjectTransferBatch(props.projectId, payloads);
    closeConfirmTransferModal();
    emits("executed");
  } catch (error) {
    handleApiError(error, "Could not complete transfer.");
  } finally {
    executeLoading.value = false;
  }
};

const closeConfirmTransferModal = (): void => {
  confirmTransferVisible.value = false;
  confirmTransferTitle.value = "";
  confirmTransferSummaryItems.value = [];
  confirmTransferMappings.value = [];
  pendingTransferPayloads.value = [];
};

const confirmExecuteTransfer = async (): Promise<void> => {
  await executePayloads(pendingTransferPayloads.value);
};

const handleExecute = async (): Promise<void> => {
  const payloads = await previewPayloads();
  if (!payloads?.length) {
    return;
  }
  const confirmState = buildConfirmStateFromPayloads(payloads);
  pendingTransferPayloads.value = payloads;
  confirmTransferTitle.value = confirmState.title;
  confirmTransferSummaryItems.value = confirmState.summaryItems;
  confirmTransferMappings.value = confirmState.mappings;
  confirmTransferVisible.value = true;
};

const handleApiError = (error: unknown, defaultMessage: string): void => {
  const parsedError = error as ApiError;
  const { message } = extractApiErrorMessage(parsedError, defaultMessage);
  backendErrorMessage.value = message;
  backendValidationErrors.value = extractValidationErrors(parsedError);
};

const extractValidationErrors = (error: ApiError): Array<string> => {
  const rawErrors = error.data?.errors;
  if (!rawErrors || typeof rawErrors === "string") {
    return [];
  }
  if (Array.isArray(rawErrors)) {
    return rawErrors
      .filter((message): message is string => typeof message === "string")
      .filter((message) => message.trim().length > 0);
  }
  if (typeof rawErrors !== "object") {
    return [];
  }
  return Object.entries(rawErrors).flatMap(([field, messages]) => {
    if (Array.isArray(messages)) {
      return messages
        .filter((msg): msg is string => typeof msg === "string")
        .map((msg) => `${field} ${msg}`);
    }
    if (typeof messages === "string") {
      return [`${field} ${messages}`];
    }
    return [];
  });
};

const resetState = (): void => {
  form.value = {};
  backendErrorMessage.value = "";
  backendValidationErrors.value = [];
  closeConfirmTransferModal();
};

const clearErrors = (): void => {
  backendErrorMessage.value = "";
  backendValidationErrors.value = [];
};

const closeModal = (): void => {
  resetState();
  emits("close");
};
</script>
